/**
 * **每个视图的「数据就绪度」**（2026-10-05 第十七批）。
 *
 * 一处取数（会话级缓存 `store/readinessStore.ts`）+ 一处判定（契约纯函数
 * `dataReadiness`），页面只负责渲染。返回：
 *
 *  · `range`：**生效区间**——「最近 N 天」已经按就绪右端对齐（`lib/readinessRange.ts`），
 *    页面据此取数即可，不需要自己再算一遍默认值；
 *  · `readyThroughDate`：该视图「数据齐到哪天」（`null` = 无法确认，绝不折成今天）；
 *  · `readiness`：契约纯函数的完整输出（`pendingDates` / `missingDates` / `summary`…）；
 *  · `showsSettlementWarning`：区间右端晚于就绪右端（或无法确认）→ 页面顶部出提示条；
 *  · `settlingNotes`：需要标注"几点齐"的平台（用于「Meta 01:00 齐、Google 05:30 齐」）。
 */

import { useEffect, useMemo } from "react";
import {
  dataReadiness,
  type DataReadiness,
  type IsoDate,
  type PlatformReadinessObservation,
  type ReadinessViewKind,
} from "@ams/contracts";
import { useReadinessStore } from "../store/readinessStore.js";
import {
  effectiveRangeFrom,
  settlementFlag,
  unsettledWindowNote,
  type SettlingPlatformNote,
  type UnsettledWindowNote,
} from "../lib/readinessRange.js";
import { parseLocalIsoDate, todayIso } from "../lib/dateRange.js";
import type { DataReadinessData } from "../lib/apiTypes.js";
import type { ResourceState } from "./useApiResource.js";

export type { SettlingPlatformNote } from "../lib/readinessRange.js";

export interface ViewReadiness {
  readonly state: ResourceState;
  readonly error: unknown;
  readonly reload: () => void;
  readonly payload: DataReadinessData | null;
  readonly readiness: DataReadiness | null;
  readonly readyThroughDate: IsoDate | null;
  /** 生效区间（「最近 N 天」已对齐到就绪右端；其它档位原样）。 */
  readonly range: { readonly from: string; readonly to: string; readonly readinessApplied: boolean; readonly includeUnsettled: boolean };
  readonly showsSettlementWarning: boolean;
  readonly settlingNotes: readonly SettlingPlatformNote[];
  /** 「含未结算」开关（来自 URL）。 */
  readonly includeUnsettled: boolean;
  /** 一致的窗口标注/告警文案（默认口径与含未结算口径共用同一纯函数）。 */
  readonly unsettledNote: UnsettledWindowNote;
  /**
   * 标注用的就绪度（右端**延伸到今天**）：`pendingDates` / `missingDates` 因此包含
   * 「窗口之后仍在结算的日子」（用户要求窗口旁写「10-04、10-05 仍在结算」）。
   */
  readonly noteReadiness: DataReadiness | null;
}

export function useDataReadiness(input: {
  readonly viewKind: ReadinessViewKind;
  /** URL 里的档位 key（`last7` / `last30` / `today` / `custom`…）。 */
  readonly rangeKey: string;
  /** 路由解析出的兜底区间（就绪度到达前 / 非「最近 N 天」档位用它）。 */
  readonly from: string;
  readonly to: string;
  /** 「含未结算」开关（URL 的 `includeUnsettled`；缺省 false = 保守口径）。 */
  readonly includeUnsettled?: boolean;
  /** 现算用的「今天」（缺省 = 真实当下；测试可注入固定值）。 */
  readonly now?: Date;
}): ViewReadiness {
  const payload = useReadinessStore((store) => store.data);
  const state = useReadinessStore((store) => store.state);
  const error = useReadinessStore((store) => store.error);
  const load = useReadinessStore((store) => store.load);
  const reload = useReadinessStore((store) => store.reload);
  const includeUnsettled = input.includeUnsettled === true;

  useEffect(() => {
    load();
  }, [load]);

  // 用「今天」的 ISO 字符串作为 memo 依赖：同一天内多次渲染不会因为 `new Date()`
  // 的实例身份变化而重算区间。
  const todayLabel = todayIso(input.now ?? new Date());
  const now = useMemo(() => parseLocalIsoDate(todayLabel) ?? new Date(), [todayLabel]);

  const observations: readonly PlatformReadinessObservation[] = useMemo(
    () =>
      payload === null
        ? []
        : payload.platforms.map((row) => ({
            platform: row.platform,
            latestDataDate: row.latestDataDate,
            expectedDataDate: row.expectedDataDate,
          })),
    [payload],
  );

  // 第一步：不带区间求就绪右端（与区间无关，只由平台状态决定）。
  const base = useMemo(
    () =>
      payload === null
        ? null
        : dataReadiness({
            viewKind: input.viewKind,
            nowIso: payload.asOf,
            opsTimeZone: payload.opsTimeZone,
            platforms: observations,
          }),
    [payload, observations, input.viewKind],
  );
  const readyThroughDate = base?.readyThroughDate ?? null;

  // 第二步：用就绪右端（或「含未结算」时的今天）解析出生效区间。
  const range = useMemo(
    () =>
      effectiveRangeFrom({
        rangeKey: input.rangeKey,
        from: input.from,
        to: input.to,
        now,
        readyThroughDate,
        includeUnsettled,
        today: base?.opsToday ?? null,
      }),
    [input.rangeKey, input.from, input.to, now, readyThroughDate, includeUnsettled, base],
  );

  // 第三步：用**生效区间**求 pending / missing / settled（提示条需要的明细）。
  const readiness = useMemo(
    () =>
      payload === null
        ? null
        : dataReadiness({
            viewKind: input.viewKind,
            nowIso: payload.asOf,
            opsTimeZone: payload.opsTimeZone,
            platforms: observations,
            rangeStart: range.from,
            rangeEnd: range.to,
          }),
    [payload, observations, input.viewKind, range.from, range.to],
  );

  /**
   * 第四步（任务 4）：**窗口标注用的**就绪度——右端**延伸到今天**。
   *
   * 为什么：默认口径的窗口右端 = 就绪右端（例如 10-03）。用户原话要求窗口旁显示
   * 「数据齐到 2026-10-03；**10-04、10-05 仍在结算**」——这两天的信息在
   * 「只算到窗口右端」的 `readiness` 里是看不到的（它们在窗口之外）。
   * 因此标注用一份右端=今天的就绪度，数据窗口仍然用 `range`（保守右端）。
   */
  const noteReadiness = useMemo(() => {
    if (payload === null) return null;
    const opsToday = base?.opsToday ?? null;
    const noteRangeEnd = opsToday !== null && opsToday > range.to ? opsToday : range.to;
    return dataReadiness({
      viewKind: input.viewKind,
      nowIso: payload.asOf,
      opsTimeZone: payload.opsTimeZone,
      platforms: observations,
      rangeStart: range.from,
      rangeEnd: noteRangeEnd,
    });
  }, [payload, observations, input.viewKind, base, range.from, range.to]);

  /**
   * 提示条触发条件（用户原话：「主动选到今天/昨天（或任何晚于 readyThroughDate 的区间）
   * 时」）+ 需要标注"几点齐"的平台——纯函数判定，见 `lib/readinessRange.ts`。
   */
  const flag = useMemo(
    () =>
      settlementFlag({
        readyThroughDate,
        rangeEnd: range.to,
        pendingPlatforms: readiness?.pendingPlatforms ?? [],
        platforms:
          payload === null
            ? []
            : payload.platforms.map((row) => ({
                platform: row.platform,
                label: row.label,
                settleClock: row.settleClock,
              })),
      }),
    [readyThroughDate, range.to, readiness, payload],
  );

  /**
   * 窗口标注（默认口径 / 含未结算口径共用同一段文案）。`opsToday` 来自就绪度端点，
   * `range.to` 是生效右端——开关打开且就绪度已知时它就等于今天。
   */
  const unsettledNote = useMemo(
    () =>
      unsettledWindowNote({
        includeUnsettled,
        rangeEnd: range.to,
        readyThroughDate,
        opsToday: base?.opsToday ?? null,
        pendingDates: noteReadiness?.pendingDates ?? [],
        missingDates: noteReadiness?.missingDates ?? [],
      }),
    [includeUnsettled, range.to, readyThroughDate, base, noteReadiness],
  );

  return {
    state,
    error,
    reload,
    payload,
    readiness,
    readyThroughDate,
    range,
    showsSettlementWarning: flag.showsSettlementWarning,
    settlingNotes: flag.settlingNotes,
    includeUnsettled,
    unsettledNote,
    noteReadiness,
  };
}
